import { HttpClient, HttpEventType, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, Subject, Subscription, of, throwError } from "rxjs";
import { map, tap, finalize, shareReplay, catchError } from "rxjs/operators";
import { ChatRequest, DoctorInfo, RolePlayRequest, RolePlayResponse, UserProfile } from "../model/rolePlay.model";
import { CreateChatReqRes, AiChatLLMRequest, DownloadFileRequest,  
    SendChatResponse, AssignScenario, CreateFeedbackMetadatRequest, UpdateFeedbackMetadataRequest, ReferenceLinkRequest } from "../model/chat.model";

import { CHAT_SERVICE, AUTH_SERVICE, CONVERSATION_SERVICE, DOCUMENTS_SERVICE,
    SUMMARIZATION_SERVICE, ADMIN_SERVICE, SEARCH_SERVICE,
    ROLEPLAY_SERVICE} from "./service-url";
import { UserProfileResponse } from "../model/shared/user.model";
import { EncryptionService } from "./encryption/encryption.service";
import { PlatformRegionService } from "./shared/platform-region.service";
import { skipSpinnerOptions } from "../v2/core/services/http-options.utils";

@Injectable({
    providedIn: 'root'
})
export class ChatService {
    http: any;
    params: HttpParams;
    streamedDataSubscription: any = new Subscription();
    summarizerDocExpDate$ = new Subject();
    private readonly trendingTopicsInFlight = new Map<string, Observable<any>>();
    // Menu structure and market lists are static reference data; several
    // components request them independently during page load. One shared,
    // replayed request per distinct payload serves all subscribers; failed
    // requests are evicted so the next call retries.
    private readonly menuDetailsCache = new Map<string, Observable<any>>();
    private readonly primaryMarketCache = new Map<string, Observable<any>>();
    private readonly chatHistoryListInFlight = new Map<string, Observable<any>>();
    constructor(
        private httpClient: HttpClient, 
        private encryptionService: EncryptionService,
        private platformRegionService: PlatformRegionService
    ) { }

    public chatResponse(request: ChatRequest): Observable<String> {
        if (request) {
            return this.httpClient.post(CHAT_SERVICE.getResponse, request,{ responseType: 'text' });
        }
        else {
            return null;
        }
    }

    public search(searchQuery:string): Observable<String> {
        let params = new HttpParams();
        params = params.append('query', searchQuery);
        if (searchQuery) {
            return this.httpClient.get(SEARCH_SERVICE.getSearchResponse, {params, responseType: 'text' });
        }
        else {
            return null;
        }
    }
    public productDataSource(business_unit_code: string): Observable<any> {
        return this.httpClient.get(ADMIN_SERVICE.productDataSource + business_unit_code);
    }

    public datasourceCode(business_unit_code: string): Observable<any>{
        return this.httpClient.get(ADMIN_SERVICE.datasourceCode + business_unit_code)
    }

 /*    public promptWithProfile(request: AiChatLLMRequest):Observable<any> {
        if (request) {
           return this.httpClient.post(CHAT_SERVICE.getChatStream, request , {observe: "events",
           responseType: "text"});
        }
        else {
            return null;
        }
    } */

    public postRefernceData (request: ReferenceLinkRequest, chatType: string) {
        if (chatType !== 'SM') {
            return this.httpClient.post(CHAT_SERVICE.getReferenceResponse, request);
        } else {
            return this.httpClient.post(SUMMARIZATION_SERVICE.getSummaryResponseOverlap, request);
        }
        
    }

    public fetchDataById(id: number): Observable<any> {
        return this.httpClient.get<any>(ROLEPLAY_SERVICE.getDoctorDetail + id);
      }

    public updateItem(id: number, item: any): Observable<any> {
        return this.httpClient.put<any>(ROLEPLAY_SERVICE.updateDoctorProfile + id, item);
      }  
    

     public getStreamedData(request: any, condition: string): Observable<any> {
        const subject = new Subject<any>();
        let endpoint: string;
        if (condition === 'EMAIL_COACH') {
            endpoint = CHAT_SERVICE.getEmailCoachResponse;
        }
        else {
            endpoint = CHAT_SERVICE.getChatStream;
        }

      this.streamedDataSubscription = this.httpClient.post(endpoint, request, { responseType: 'text', observe: 'events', reportProgress: true })
            .subscribe(event => {
                if (event.type === HttpEventType.DownloadProgress && event['partialText']) {
                    subject.next(event['partialText']);
                } else if (event.type === HttpEventType.Response) {
                    subject.complete();
                }
            });

        return subject.asObservable();
    }

    /**
     * SSE-based streaming method for agent_ai_chat_llm_response_stream.
     * Parses newline-delimited JSON events: progress, answer, references, followup, synthetic, cost.
     * Emits parsed { event, data } objects to subscribers.
     */
    public getSSEStreamedData(request: any, condition: string): Observable<{ event: string; data: any }> {
        let endpoint = CHAT_SERVICE.getChatStream;

        return new Observable<{ event: string; data: any }>((observer) => {
            const controller = new AbortController();
            const signal = controller.signal;

            const { bearer_token, ...payload } = request;
            const headers: Record<string, string> = {
                'Content-Type': 'application/json'
            };
            const token = bearer_token || localStorage.getItem('bearer_token');
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
                signal
            })
            .then(async response => {
                if (!response.ok) {
                    const text = await response.text();
                    observer.error(new Error(`Stream failed: ${response.status} ${response.statusText} - ${text}`));
                    return;
                }
                if (!response.body) {
                    const text = await response.text();
                    // Try to parse as newline-delimited JSON
                    const lines = text.split('\n').filter(l => l.trim());
                    lines.forEach(line => {
                        try {
                            const parsed = JSON.parse(line);
                            observer.next(parsed);
                        } catch { /* skip non-JSON lines */ }
                    });
                    observer.complete();
                    return;
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                function readChunk(): void {
                    reader.read()
                        .then(({ done, value }) => {
                            if (done) {
                                // Process any remaining buffer
                                if (buffer.trim()) {
                                    const lines = buffer.split('\n').filter(l => l.trim());
                                    lines.forEach(line => {
                                        try {
                                            const parsed = JSON.parse(line);
                                            observer.next(parsed);
                                        } catch { /* skip */ }
                                    });
                                }
                                observer.complete();
                                return;
                            }
                            buffer += decoder.decode(value, { stream: true });
                            // Process complete lines
                            const lines = buffer.split('\n');
                            buffer = lines.pop() || ''; // Keep incomplete last line in buffer
                            lines.forEach(line => {
                                const trimmed = line.trim();
                                if (trimmed) {
                                    try {
                                        const parsed = JSON.parse(trimmed);
                                        observer.next(parsed);
                                    } catch { /* skip non-JSON lines */ }
                                }
                            });
                            readChunk();
                        })
                        .catch(err => observer.error(err));
                }

                readChunk();
            })
            .catch(err => {
                observer.error(err);
            });

            return () => {
                try { controller.abort(); } catch (e) { /* ignore */ }
            };
        });
    }


    public createProfile(request:RolePlayRequest):Observable<any> {
        if (request) {
            return this.httpClient.post(AUTH_SERVICE.createProfile, request);
        }
        else {
            return null;
        }
    }

    public createFeedbackMetadata(request: CreateFeedbackMetadatRequest, summaryType: string): Observable<any> {
        if (summaryType === 'TEXTSUMMARY' || summaryType === 'summary' || summaryType === 'FILESUMMARY') {
            return this.httpClient.post(SUMMARIZATION_SERVICE.createFeedbackDataFileOrText, request);
        } else if (summaryType === 'summary qna' || summaryType === 'QNASUMMARY') {
            return this.httpClient.post(SUMMARIZATION_SERVICE.createFeedbackDateForQNA, request);
        } else {
            request['region'] = [this.platformRegionService.selected_region_code];
            request['primary_market'] = this.platformRegionService.selected_market_code.split(',');
            return this.httpClient.post(CONVERSATION_SERVICE.createFeedbackMetadata, request);
        }
    }

    public getFeedbackMetadata(id: number, summaryType: string): Observable<any> {
        if(summaryType === 'TEXTSUMMARY' || summaryType === 'FILESUMMARY') {
            return this.httpClient.get<any[]>(`${SUMMARIZATION_SERVICE.getSummaryFeedbackDataFileOrText}/${id}`);
        } else if(summaryType === 'QNASUMMARY') {
            return this.httpClient.get<any[]>(`${SUMMARIZATION_SERVICE.getSummaryFeedbackDataQNA}/${id}`);
        } else {
            return this.httpClient.get<any[]>(`${CONVERSATION_SERVICE.getFeedbackMetadata}/${id}`);
        }
      }

      public updateFeedbackMetadata(id: number, request: UpdateFeedbackMetadataRequest, summaryType: string):Observable<any> {
        if(summaryType === 'TEXTSUMMARY' || summaryType === 'FILESUMMARY') {
            return this.httpClient.put(`${SUMMARIZATION_SERVICE.updateSummaryFeedbackDataFileOrText}/${id}`, request);
        } else if(summaryType === 'QNASUMMARY') {
            return this.httpClient.put(`${SUMMARIZATION_SERVICE.updateSummaryFeedbackDataQNA}/${id}`, request);
        } else {
            return this.httpClient.put(`${CONVERSATION_SERVICE.updateFeedbackMetadata}/${id}`, request);
        }
      }

      public createDoctorProfile(request:any):Observable<any>{
        if(request){
            return this.httpClient.post(ROLEPLAY_SERVICE.createDoctorProfile, request);
        }else{
            return null;
        }
    }
    public deleteDrProfile(profile_id :number):Observable<any>{
        return this.httpClient.delete(ROLEPLAY_SERVICE.deleteDoctorProfile + profile_id);
    }

    /**Create Chat Session API - Dashboard page of Trainee */
    public createChatSession(request: CreateChatReqRes):Observable<any> {
        if (request) {
           return this.httpClient.post(CONVERSATION_SERVICE.createChatSession, request);
        }
        else {
            return null;
        }
    }
   
     /**Create Chat Session API - Dashboard page of Trainee */
     public sendChatResponse(request: SendChatResponse, chatType: string):Observable<any> {
        if (chatType !== 'SM') {
            return this.httpClient.post(CONVERSATION_SERVICE.SendChatResponse, request);
        }
        else {
            return this.httpClient.post(SUMMARIZATION_SERVICE.sendSummaryChatResponse, request);
        }
    }

    public updateUserProfile(payload: any,wwid:number): Observable<any> {
        return this.httpClient.put(AUTH_SERVICE.updateUserProfile+ '/'+ wwid,payload);
    }

    public updateDefaultBUMarket(payload: any): Observable<any> {
        return this.httpClient.post(AUTH_SERVICE.defaultBUMarketCreateUpdate,payload);
    }

    public getlastLogin(wwid: string): Observable<any> {
        return this.httpClient.put(`${ADMIN_SERVICE.lastloginDetails}/${wwid}`, null); 
    }
    

    /**Summarizer - File upload*/
    public summarizerFileUpload(file: File, request: any):Observable<any> {
        const formData: FormData = new FormData();
        formData.append('file', file, file.name);
        if (file) {
           return this.httpClient.post(SUMMARIZATION_SERVICE.summarizerFileUpload + '?chat_user_id=' + request.chat_user_id + '&expiry_date='
             + request.expiry_date+'&chat_session_id=' + request.chat_session_id + '&business_unit_code='+ request.business_unit_code, formData);
        }
        else {
            return null;
        }
    }
    public uploadFileStream(file: File, request: any): Observable<string> {
        if (!file) {
            return of(null);
        }

        const url = SEARCH_SERVICE.uploadDocument;
        const deviceType = request?.device_type || (this.isRunningInBrowserTab() ? 'web' : 'desktop');

        const formData = new FormData();
        formData.append('uploaded_file', file, file.name);
        formData.append('wwid', String(request.chat_user_id));
        formData.append('chat_session_id', String(request.chat_session_id));
        formData.append('device_type', String(deviceType));
        formData.append('business_unit_code', String(request.business_unit_code));
        // JHUD-5268: only sent for image uploads (JPEG/JPG/PNG), populated by the caller
        if (request.user_request) {
            formData.append('user_request', String(request.user_request));
        }

        return new Observable<string>((observer) => {
            const controller = new AbortController();
            const signal = controller.signal;

            const headers: Record<string, string> = {};
            const token = localStorage.getItem('bearer_token');
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            fetch(url, {
                method: 'POST',
                headers,
                body: formData,
                signal
            })
                .then(async response => {
                    if (!response.ok) {
                        const t = await response.text();
                        observer.error(new Error(`Upload failed: ${response.status} ${response.statusText} - ${t}`));
                    }
                    if (!response.body) {
                        const text = await response.text();
                        observer.next(text);
                        observer.complete();
                    }

                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();

                    function readChunk(): void {
                        reader.read()
                            .then(({ done, value }) => {
                                if (done) {
                                    observer.complete();
                                    return;
                                }
                                const chunkText = decoder.decode(value, { stream: true });
                                observer.next(chunkText);
                                readChunk();
                            })
                            .catch(err => observer.error(err));
                    }

                    readChunk();
                })
                .catch(err => {
                    observer.error(err);
                });

            return () => {
                try { controller.abort(); } catch (e) { }
            };
        });
    }

    /**Summarizer - Text Content*/
    public summarizerTextContent(request: any):Observable<any> {
        if (request) {
           return this.httpClient.post(SUMMARIZATION_SERVICE.summarizerTextContent, request);
        }
        else {
            return null;
        }
    }

    public summarizerQandA(request: any):Observable<any> {
       // return this.httpClient.post(SUMMARIZATION_SERVICE.summarizerQandA,  request);
        const subject = new Subject<any>();
        let endpoint: string;
        this.httpClient.post(SUMMARIZATION_SERVICE.summarizerQandA, request, { responseType: 'text', observe: 'events', reportProgress: true })
            .subscribe(event => {
                if (event.type === HttpEventType.DownloadProgress && event['partialText']) {
                    subject.next(event['partialText']);
                } else if (event.type === HttpEventType.Response) {
                    subject.complete();
                }
            });

        return subject.asObservable();
    }

     /**Create Chat Session API - Dashboard page of Trainee */
     public getDownloadFileInfo(request: DownloadFileRequest):Observable<any> {
        return this.httpClient.post(DOCUMENTS_SERVICE.downloadFile, request, { responseType: 'blob' }).pipe(
            tap((response: Blob) => {
              const a = document.createElement('a');
              const objectUrl = URL.createObjectURL(response);
              a.href = objectUrl;
              a.target = '_blank';
              a.download = request.document_name;
              a.click();
              URL.revokeObjectURL(objectUrl);
            })
          );
    }
    public getReferenceDownloadUrl(request: DownloadFileRequest, chatStatus):Observable<any> {
        if(chatStatus == 'SM'){
            return this.httpClient.post(SUMMARIZATION_SERVICE.getDownloadUrl, request);
        }else{
            return this.httpClient.post(CHAT_SERVICE.getDownloadUrl2, request);
        }
    }

    downloadFileForLocal(url: string, filename: string, header: any): void {
        const headers = new HttpHeaders(header);

         this.httpClient.get(url, { headers, responseType: 'blob' as 'json' }).pipe(
            tap((response: any) => {
              const blob = new Blob([response], { type: 'application/octet-stream' });
              const a = document.createElement('a');
              const objectUrl = URL.createObjectURL(blob);
              a.href = objectUrl;
              a.download = filename;
              a.click();
              URL.revokeObjectURL(objectUrl);
            })
          ).subscribe({
            error: (err) => console.error('Download error:', err)
          }); 
          
        }
     /** Assign the scenario to multiple users*/
     public assignScenario(request: AssignScenario):Observable<any> {
        if (request) {
           return this.httpClient.post(ROLEPLAY_SERVICE.assignScenario, request);
        }
        else {
            return null;
        }
    }

    /** Get chat history list to display on the sidebar */
    public chatHistoryList(userId:number=0, length: number=0, chatType:string="", scenarioId: number=0, subType: string="",region: string): Observable<any> {
        const code = sessionStorage.getItem("businessUnitCode");
        let url: string;
        if(chatType == 'role-play' && scenarioId > 0) {
            // return this.httpClient.get(`${CONVERSATION_SERVICE.getChatSessionGroplistRoleplay}/${userId}/${scenarioId}?limit=${length}&chat_type=${chatType}`);
            url = `${CONVERSATION_SERVICE.getChatSessionGroplist}/${userId}?business_unit_code=${code}&limit=${length}&chat_type=${chatType}&region_name=${region}`;
        }
        else if(subType !== "" && chatType !== 'role-play') {
            url = `${CONVERSATION_SERVICE.getChatSessionGroplist}/${userId}?business_unit_code=${code}&limit=${length}&chat_type=${chatType}&chat_sub_type=${subType}&region_name=${region}`;
        }
        else{
            url = `${CONVERSATION_SERVICE.getChatSessionGroplist}/${userId}?business_unit_code=${code}&limit=${length}&chat_type=${chatType}&region_name=${region}`;
        }
        // Chat history is dynamic, so it is never cached — but identical
        // requests fired concurrently (e.g. two components loading the
        // sidebar list at once) share a single in-flight HTTP call. The
        // entry is removed on completion, so the next call refetches.
        const inFlight = this.chatHistoryListInFlight.get(url);
        if (inFlight) {
            return inFlight;
        }
        const request$ = this.httpClient.get(url).pipe(
            finalize(() => this.chatHistoryListInFlight.delete(url)),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        this.chatHistoryListInFlight.set(url, request$);
        return request$;
    }
   

    /** Get the trainee list based on the scenario ID */
    public getTraineeListByScenarioId(scenarioId:number, bu:string, region): Observable<any> {
        if (scenarioId) {
            return this.httpClient.get(ROLEPLAY_SERVICE.getTraineeListByScenarioId + '/' + scenarioId + '?business_unit_code=' + bu + '&region=' + region);
        }
        else {
            return null;
        }
    }

    /** Get chat history details based on the session id - when we click on sidebar for particular chat */
    public getChatDetailsBySessionId(chatSessionId: number, chatType: string): Observable<any> {
        if (chatType === 'SUMMARIZER') {
            return this.httpClient.get(SUMMARIZATION_SERVICE.getSummaryChatDetails + '/' + chatSessionId);
        }
        else {
            return this.httpClient.get(CONVERSATION_SERVICE.getChatDetails + '/' + chatSessionId + '?chat_type=' + chatType);        
        }
    }

    /** Get top trending topics to display on chat screen */
    public trendingTopics( business_unit_code:string, region: string): Observable<any> {
            const key = `${business_unit_code}|${region}`;
            const inFlight = this.trendingTopicsInFlight.get(key);
            if (inFlight) {
                return inFlight;
            }
            const request$ = this.httpClient.get<any>(CONVERSATION_SERVICE.trendingTopicsByUnitCode+ '/'+ business_unit_code + `?region_name=${region}`).pipe(
                map(val => {
                    return val;
                }),
                finalize(() => this.trendingTopicsInFlight.delete(key)),
                shareReplay({ bufferSize: 1, refCount: false })
            );
            this.trendingTopicsInFlight.set(key, request$);
            return request$;
    }

    public createUserProfile(request:UserProfile):Observable<any> {
        if (request) {
            return this.httpClient.post(AUTH_SERVICE.createProfile, request);
        }
        else {
            return null;
        }
    }

    isRunningInBrowserTab():boolean {
        return window.matchMedia('(display-mode:browser)').matches;
    }


    public getUserProfileByWWID():Observable<UserProfileResponse> {
        const userId = Number(sessionStorage.getItem('userId'));
        const decryptText = this.encryptionService.decrypt(sessionStorage.getItem('groups'));
        const groups = JSON.parse(decryptText);

        const payload = {
            wwid: userId,
            role_code: groups
        }

        if (this.isRunningInBrowserTab()) {
            payload['device_type'] = 'web'
        }

        return this.httpClient.post<UserProfileResponse>(AUTH_SERVICE.getUserProfile, payload);
    }

    public accessRequest(payload: any, wwid: number): Observable<any> {
        if (wwid) {
           const userName = encodeURIComponent(payload.user_name);
           const userEmail = encodeURIComponent(payload.user_email);
           const businessUnitCode = encodeURIComponent(payload.business_unit_code);
           const requestAccessUrl = `${ADMIN_SERVICE.user_request}/`+ wwid +`?user_name=${userName}&user_email=${userEmail}&business_unit_code=${businessUnitCode}`;
           return this.httpClient.post(requestAccessUrl, payload);
        } else {
            return null;
        }
    }
    public getProductList():Observable<any>{
        return this.httpClient.get(ADMIN_SERVICE.ProductList)
    }

    public getMenuDetails(request: any): Observable<any> {
        const key = JSON.stringify(request ?? {});
        const cached = this.menuDetailsCache.get(key);
        if (cached) {
            return cached;
        }
        const request$ = this.httpClient.post(ADMIN_SERVICE.getMenuDetails, request).pipe(
            catchError(err => {
                this.menuDetailsCache.delete(key);
                return throwError(() => err);
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        this.menuDetailsCache.set(key, request$);
        return request$;
    }

    public getJSON(): Observable<any> {
        return this.httpClient.get("./assets/help.json");
    }

    getPdfReport(url: string, mimeType: string) {
        return this.httpClient.get(url, {...{ responseType: 'blob', observe: 'response'},...skipSpinnerOptions()}).pipe(
          map((res: any) => {
            return new Blob([res.body], { type: mimeType });
          })
        );
    }

    public getRole(): Observable<any> {
        return this.httpClient.get(AUTH_SERVICE.userRoleLabel);
    }
    public getRoleCodeList(businessUnitCode?, regionName?): Observable<any> {
        if (regionName && businessUnitCode) {
            return this.httpClient.get(AUTH_SERVICE.userRoleList + '?business_unit_code=' + businessUnitCode + `&region=${regionName}`);
        } else {
            return this.httpClient.get(AUTH_SERVICE.userRoleList)
        }
    }

      public getProductMetaData(businessUnitCode): Observable<any> {
        return this.httpClient.get(ADMIN_SERVICE.getProductMetaData+'/'+businessUnitCode );
     }
      public getPrimaryRegion(businessUnitCode): Observable<any> {
        return this.httpClient.get(ADMIN_SERVICE.getRegionList+'?business_unit_code='+businessUnitCode );
     }
     public getPrimaryMarket(regionName?:string):Observable<any>{
        const key = regionName || '';
        const cached = this.primaryMarketCache.get(key);
        if (cached) {
            return cached;
        }
        const url = regionName
            ? ADMIN_SERVICE.getDetailMarketList + `?region_name=${regionName}`
            : ADMIN_SERVICE.getDetailMarketList;
        const request$ = this.httpClient.get(url).pipe(
            catchError(err => {
                this.primaryMarketCache.delete(key);
                return throwError(() => err);
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        this.primaryMarketCache.set(key, request$);
        return request$;
     }

     public getBusinessUnitByCode(code):Observable<any>{
        return this.httpClient.get(ADMIN_SERVICE.referenceFilePath+"/"+code, skipSpinnerOptions());
     }
    public getTokenCount(file: File, request: any):Observable<any> {
        const formData: FormData = new FormData();
        formData.append('file', file, file.name);
        if (file) {
           return this.httpClient.post(CHAT_SERVICE.tokenCount + '?chat_user_id=' + request.chat_user_id + '&business_unit_code='+ request.business_unit_code, formData);
        }
        else {
            return null;
        }
    }
    public summarizeBackground(file: File, request: any):Observable<any>{
        const formData: FormData = new FormData();
        formData.append('file', file, file.name);
        if (file) {
           return this.httpClient.post(SUMMARIZATION_SERVICE.summarizeFileBackground + '?chat_user_id=' + request.chat_user_id + '&business_unit_code='+ request.business_unit_code + '&chat_session_id=' + request.chat_session_id +'&user_response=' + request.user_response + '&expiry_date='
             + request.expiry_date, formData);
        }
        else {
            return null;
        }
    }
    public getBusinnessFaq(businessUnitCode?, regionName?): Observable<any> {
        return this.httpClient.get(ADMIN_SERVICE.getQuestionList+'/'+businessUnitCode + `?region_name=${regionName}` );
     }
     public createFAaq(request: any): Observable<any> {
        return this.httpClient.post(ADMIN_SERVICE.createQuestion, request);
    }
    public updateQuestionStatus(request):Observable<any>{
        return this.httpClient.post(ADMIN_SERVICE.changeStatus + '?faq_id=' +request.faq_id + '&new_status='+request.new_status + '&modified_by=' +request.modified_by,null);

    }
    public editQuestion(id:number,request): Observable<any>{
        return this.httpClient.put<any>(ADMIN_SERVICE.updateQuestion + id, request);
    }

    public trackReferenceClick(payload: { chunk_id: string, chat_history_id: any }): Observable<any> {
        return this.httpClient.post(CONVERSATION_SERVICE.referenceClick, payload);
    }

     public verifyShowPadUser(payload: any): Observable<any> {
           const userName = encodeURIComponent(payload.user_name);
           const userEmail = encodeURIComponent(payload.user_email);
           return this.httpClient.post(AUTH_SERVICE.showPadUserVerification, payload);
    }

    public deleteAssignment(id:number): Observable<any> {
        return this.httpClient.delete(`${ROLEPLAY_SERVICE.deleteAssignment}/${id}`);
    }

} 